import { toast } from "react-toastify";
import {
  CreateLocalTrackOptions,
  LocalAudioTrack,
  LocalVideoTrack,
  Room,
} from "twilio-video";

import { ReactNode, createContext } from "react";

import { useAudioAndVideoLocalTracks } from "hooks/liveMeeting/useAudioAndVideoLocalTracks";
import { useHandleRoomDisconnection } from "hooks/liveMeeting/useHandleRoomDisconnection";
import { useLocalTracks } from "hooks/liveMeeting/useLocalTracks";
import { useRestartAudioTrackOnDeviceChange } from "hooks/liveMeeting/useRestartAudioTrackOnDeviceChange";
import { useRoom } from "hooks/liveMeeting/useRoom";
import { useScreenShareToggle } from "hooks/liveMeeting/useScreenShareToggle";

import { SingleVoting } from "store/voting/types";

export type SidebarContent = "participants" | "chat" | "voting" | null;

export type IVideoContext = {
  room: Room | null;
  localTracks: (LocalAudioTrack | LocalVideoTrack)[];
  onError: ErrorCallback;
  getLocalVideoTrack: (
    newOptions?: CreateLocalTrackOptions
  ) => Promise<LocalVideoTrack>;
  getLocalAudioTrack: (deviceId?: string) => Promise<LocalAudioTrack>;
  isAcquiringLocalTracks: boolean;
  removeLocalVideoTrack: () => void;
  removeLocalAudioTrack: () => void;
  getAudioAndVideoTracks: () => Promise<void>;
  setSidebarContent: (content: SidebarContent) => void;
  sidebarContent: SidebarContent;
  agendaOpened: boolean;
  setAgendaOpened: (opened: boolean) => void;
  liveVoting: SingleVoting | null;
  setLiveVoting: (voting: SingleVoting | null) => void;
  isSharingScreen: boolean;
  toggleScreenShare: () => void;
  agendaSmall: boolean;
  setAgendaSmall: (small: boolean) => void;
};

export const VideoContext = createContext<IVideoContext>(null!);

type VideoProviderProps = {
  children: ReactNode;
};

const VideoProvider = ({ children }: VideoProviderProps) => {
  const {
    localTracks,
    getLocalVideoTrack,
    getLocalAudioTrack,
    isAcquiringLocalTracks,
    removeLocalAudioTrack,
    removeLocalVideoTrack,
    getAudioAndVideoTracks,
  } = useLocalTracks();
  const {
    room,
    setSidebarContent,
    sidebarContent,
    agendaOpened,
    setAgendaOpened,
    liveVoting,
    setLiveVoting,
    agendaSmall,
    setAgendaSmall,
  } = useRoom(localTracks, (error) => {
    console.log(error);
    toast.error(error.message);
  });

  const [isSharingScreen, toggleScreenShare] = useScreenShareToggle(
    room,
    (error) => {
      console.log(error);
      toast.error(error.message);
    }
  );

  useHandleRoomDisconnection(
    room,
    (error) => {
      console.log("error on disconnect", error);
      console.log(error);
      toast.error(error.message);
    },
    removeLocalAudioTrack,
    removeLocalVideoTrack,
    isSharingScreen,
    toggleScreenShare
  );

  useRestartAudioTrackOnDeviceChange(localTracks);

  useAudioAndVideoLocalTracks({
    room,
    getAudioAndVideoTracks,
    removeLocalAudioTrack,
    removeLocalVideoTrack,
  });

  return (
    <VideoContext.Provider
      value={{
        room,
        localTracks,
        onError: (error) => {
          console.log(error);
          toast.error(error.message);
        },
        getLocalVideoTrack,
        getLocalAudioTrack,
        isAcquiringLocalTracks,
        removeLocalVideoTrack,
        getAudioAndVideoTracks,
        setSidebarContent,
        sidebarContent,
        agendaOpened,
        setAgendaOpened,
        liveVoting,
        setLiveVoting,
        isSharingScreen,
        toggleScreenShare,
        removeLocalAudioTrack,
        agendaSmall,
        setAgendaSmall,
      }}
    >
      {children}
    </VideoContext.Provider>
  );
};

export default VideoProvider;
